
import { supabase } from '@/integrations/supabase/client';

/**
 * Find linked bot ID (bidirectional lookup between original and shadow bots)
 * Returns the linked bot ID and which one has the trading file
 */
export const findLinkedBotInfo = async (botId: string): Promise<{
  linkedBotId: string | null;
  linkedBotHasFile: boolean;
  currentBotHasFile: boolean;
  currentBotType: 'original' | 'shadow';
}> => {
  try {
    console.log('Finding linked bot info for bot:', botId);
    
    // Get current bot info
    const { data: currentBot, error: currentBotError } = await supabase
      .from('bots')
      .select('signal_token, reverse_signal_token, type, linked_trading_file_id')
      .eq('id', botId)
      .single();
    
    if (currentBotError || !currentBot) {
      console.error('Error fetching current bot:', currentBotError);
      return {
        linkedBotId: null,
        linkedBotHasFile: false,
        currentBotHasFile: false,
        currentBotType: 'original'
      };
    }

    const currentBotType: 'original' | 'shadow' = currentBot.type === 'shadow_bot' ? 'shadow' : 'original';
    const currentBotHasFile = !!currentBot.linked_trading_file_id;
    
    // Find linked bot using signal tokens
    let linkedBot = null;
    if (currentBotType === 'original') {
      // Current is original, find shadow bot
      if (currentBot.reverse_signal_token) {
        const { data } = await supabase
          .from('bots')
          .select('id, linked_trading_file_id')
          .eq('signal_token', currentBot.reverse_signal_token)
          .eq('type', 'shadow_bot')
          .single();
        linkedBot = data;
      }
    } else {
      // Current is shadow, find original bot
      if (currentBot.signal_token) {
        const { data } = await supabase
          .from('bots')
          .select('id, linked_trading_file_id')
          .eq('reverse_signal_token', currentBot.signal_token)
          .neq('type', 'shadow_bot')
          .single();
        linkedBot = data;
      }
    }
    
    const result = {
      linkedBotId: linkedBot?.id || null,
      linkedBotHasFile: !!linkedBot?.linked_trading_file_id,
      currentBotHasFile,
      currentBotType
    };
    
    console.log('Linked bot info:', result);
    return result;
  } catch (error) {
    console.error('Error in findLinkedBotInfo:', error);
    return {
      linkedBotId: null,
      linkedBotHasFile: false,
      currentBotHasFile: false,
      currentBotType: 'original'
    };
  }
};

/**
 * Find shadow bot ID for a given original bot ID (backward compatibility)
 */
export const findShadowBotId = async (originalBotId: string): Promise<string | null> => {
  const info = await findLinkedBotInfo(originalBotId);
  return info.currentBotType === 'original' ? info.linkedBotId : null;
};
